/**
 * Guest Quota Service
 * Gère les quotas des utilisateurs guests avec validation serveur via Supabase
 *
 * Sécurité:
 * - Fingerprinting navigateur pour identifier les guests
 * - Validation serveur (Supabase) avant chaque action
 * - localStorage comme cache local uniquement
 * - Impossible de contourner en effaçant localStorage
 */

import {
  getCachedFingerprint,
  getCachedStableFingerprint,
  getBrowserMetadata,
} from "./browserFingerprint";
import { logger } from "./logger";
import { logError, ErrorFactory } from "./error-handling";
import { isE2ETestingEnvironment } from "./e2e-detection";
import type { CreditActionType } from "./quotaTracking";
import { supabaseSelect, supabaseRpc } from "./supabaseApi";
import { CONVERSATION_QUOTAS } from "../constants/quotas";

// ============================================================================
// TYPES
// ============================================================================

export interface GuestQuotaData {
  id: string;
  fingerprint: string;
  conversationsCreated: number;
  // Compteurs séparés par type de poll (pollsCreated supprimé - calculer à la volée)
  datePollsCreated: number;
  formPollsCreated: number;
  quizzCreated: number;
  availabilityPollsCreated: number;
  aiMessages: number;
  analyticsQueries: number;
  simulations: number;
  totalCreditsConsumed: number;
  firstSeenAt: string;
  lastActivityAt: string;
  lastResetAt: string | null;
  browserMetadata?: {
    userAgent?: string | null;
    timezone?: string | null;
    language?: string | null;
    screenResolution?: string | null;
  };
}

/**
 * Calcule le total de polls créés à partir des compteurs séparés
 * (remplace l'ancien pollsCreated qui était maintenu par trigger SQL)
 */
export function calculateTotalPollsCreated(quota: {
  datePollsCreated: number;
  formPollsCreated: number;
  quizzCreated: number;
  availabilityPollsCreated: number;
}): number {
  return (
    quota.datePollsCreated +
    quota.formPollsCreated +
    quota.quizzCreated +
    quota.availabilityPollsCreated
  );
}

interface GuestQuotaJournalSupabaseRow {
  id: string;
  guest_quota_id: string;
  fingerprint: string;
  action: CreditActionType;
  credits: number;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export interface GuestQuotaJournalEntry {
  id: string;
  guestQuotaId: string;
  fingerprint: string;
  action: CreditActionType;
  credits: number;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const GUEST_LIMITS = {
  TOTAL_CREDITS: 100, // Crédit global pour toutes les actions combinées
  // Source de vérité unique : CONVERSATION_QUOTAS.ANONYMOUS (quotas.ts).
  // Était hardcodé à 10 ici, découplé de la doctrine (3) — le seul appelant
  // qui aurait dû s'en servir (useAutoSave.createConversation, mode invité)
  // ne faisait d'ailleurs aucun appel à canConsumeCredits/consumeGuestCredits
  // du tout, donc ce plafond n'était jamais réellement appliqué en prod.
  // Cf. correctif du 10/09/2026.
  CONVERSATIONS: CONVERSATION_QUOTAS.ANONYMOUS,
  DATE_POLLS: 5,
  FORM_POLLS: 5,
  QUIZZ: 5,
  AVAILABILITY_POLLS: 5,
  AI_MESSAGES: 100,
  ANALYTICS_QUERIES: 50,
  SIMULATIONS: 20,
} as const;

// Exposé pour l'UI afin d'aligner l'affichage global des crédits invités
export const GUEST_TOTAL_CREDITS_LIMIT = GUEST_LIMITS.TOTAL_CREDITS;

const GUEST_QUOTA_TABLE = "guest_quotas";
const GUEST_QUOTA_JOURNAL_TABLE = "guest_quota_journal";
const LOCAL_QUOTA_ID_KEY = "guest_quota_id";

interface GuestQuotaSupabaseRow {
  id: string;
  fingerprint: string;
  conversations_created: number;
  // polls_created supprimé - utiliser calculateTotalPollsCreated() pour calculer à la volée
  date_polls_created: number;
  form_polls_created: number;
  quizz_created: number;
  availability_polls_created: number;
  ai_messages: number;
  analytics_queries: number;
  simulations: number;
  total_credits_consumed: number;
  first_seen_at: string;
  last_activity_at: string;
  last_reset_at: string | null;
  user_agent?: string | null;
  timezone?: string | null;
  language?: string | null;
  screen_resolution?: string | null;
  stable_fingerprint?: string | null;
  alt_fingerprints?: string[] | null;
}

// NOTE: localStorage bypass removed due to security vulnerability (B6)
// Previous code allowed clients to set arbitrary bypass tokens.
// E2E and local testing use environment detection instead.

function shouldBypassGuestQuota(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  const isE2E =
    isE2ETestingEnvironment() ||
    (window as Window & { __IS_E2E_TESTING__?: boolean }).__IS_E2E_TESTING__ ===
      true;

  if (isE2E) {
    return true;
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || "";
  const isMockedSupabase =
    supabaseUrl.includes("localhost") || supabaseUrl.includes("127.0.0.1");

  if (isMockedSupabase) {
    logger.debug("Mock Supabase URL detected, bypassing guest quota", "quota");
  }

  return isMockedSupabase;
}

interface GuestQuotaSyncResult {
  quota: GuestQuotaData;
  fingerprint: string;
}

function mapSupabaseRowToGuestQuota(
  row: GuestQuotaSupabaseRow,
): GuestQuotaData {
  return {
    id: row.id,
    fingerprint: row.fingerprint,
    conversationsCreated: row.conversations_created,
    datePollsCreated: row.date_polls_created || 0,
    formPollsCreated: row.form_polls_created || 0,
    quizzCreated: row.quizz_created || 0,
    availabilityPollsCreated: row.availability_polls_created || 0,
    aiMessages: row.ai_messages,
    analyticsQueries: row.analytics_queries,
    simulations: row.simulations,
    totalCreditsConsumed: row.total_credits_consumed,
    firstSeenAt: row.first_seen_at,
    lastActivityAt: row.last_activity_at,
    lastResetAt: row.last_reset_at,
    browserMetadata: {
      userAgent: row.user_agent,
      timezone: row.timezone,
      language: row.language,
      screenResolution: row.screen_resolution,
    },
  };
}

function evaluateQuotaLimits(
  quota: GuestQuotaData,
  action: CreditActionType,
  credits: number,
  metadata?: Record<string, unknown>,
): { allowed: boolean; reason?: string } {
  if (quota.totalCreditsConsumed + credits > GUEST_LIMITS.TOTAL_CREDITS) {
    return {
      allowed: false,
      reason: `Total credit limit reached (${GUEST_LIMITS.TOTAL_CREDITS})`,
    };
  }

  switch (action) {
    case "conversation_created":
      if (quota.conversationsCreated >= GUEST_LIMITS.CONVERSATIONS) {
        return {
          allowed: false,
          reason: `Conversation limit reached (${GUEST_LIMITS.CONVERSATIONS})`,
        };
      }
      break;
    case "poll_created": {
      // Vérifier limite par type de poll uniquement (séparation complète par produit)
      const pollType = metadata?.pollType as
        | "date"
        | "form"
        | "quizz"
        | "availability"
        | undefined;

      // Validation stricte : pollType est obligatoire pour poll_created
      if (
        !pollType ||
        !["date", "form", "quizz", "availability"].includes(pollType)
      ) {
        return {
          allowed: false,
          reason: `pollType is required and must be one of: "date", "form", "quizz", "availability". Received: ${pollType}`,
        };
      }

      let limit: number;
      let current: number;
      switch (pollType) {
        case "date":
          limit = GUEST_LIMITS.DATE_POLLS;
          current = quota.datePollsCreated;
          break;
        case "form":
          limit = GUEST_LIMITS.FORM_POLLS;
          current = quota.formPollsCreated;
          break;
        case "quizz":
          limit = GUEST_LIMITS.QUIZZ;
          current = quota.quizzCreated;
          break;
        case "availability":
          limit = GUEST_LIMITS.AVAILABILITY_POLLS;
          current = quota.availabilityPollsCreated;
          break;
        default:
          // Type invalide, rejeter (ne devrait jamais arriver grâce à la validation ci-dessus)
          return {
            allowed: false,
            reason: `Invalid pollType: ${pollType}`,
          };
      }

      if (current >= limit) {
        return {
          allowed: false,
          reason: `${pollType} poll limit reached (${limit})`,
        };
      }
      break;
    }
    case "ai_message":
      if (quota.aiMessages >= GUEST_LIMITS.AI_MESSAGES) {
        return {
          allowed: false,
          reason: `AI message limit reached (${GUEST_LIMITS.AI_MESSAGES})`,
        };
      }
      break;
    case "analytics_query":
      if (quota.analyticsQueries >= GUEST_LIMITS.ANALYTICS_QUERIES) {
        return {
          allowed: false,
          reason: `Analytics query limit reached (${GUEST_LIMITS.ANALYTICS_QUERIES})`,
        };
      }
      break;
    case "simulation":
      if (quota.simulations >= GUEST_LIMITS.SIMULATIONS) {
        return {
          allowed: false,
          reason: `Simulation limit reached (${GUEST_LIMITS.SIMULATIONS})`,
        };
      }
      break;
    default:
      break;
  }

  return { allowed: true };
}

// Cache pour éviter les appels répétés
const quotaCache = new Map<
  string,
  { data: GuestQuotaSupabaseRow | null; timestamp: number }
>();
const CACHE_TTL = 5000; // 5 secondes

// Fonction pour vider le cache (utilisée dans les tests)
export function clearQuotaCache(): void {
  quotaCache.clear();
}

async function ensureGuestQuota(): Promise<GuestQuotaSyncResult | null> {
  if (shouldBypassGuestQuota()) {
    return null;
  }

  if (
    !import.meta.env.VITE_SUPABASE_URL ||
    !import.meta.env.VITE_SUPABASE_ANON_KEY
  ) {
    logger.warn(
      "Supabase configuration missing, using local quota fallback",
      "quota",
    );
    return null;
  }

  try {
    const fingerprint = await getCachedFingerprint();
    const stableFingerprint = await getCachedStableFingerprint().catch(
      () => "",
    );
    const metadata = getBrowserMetadata();
    const cachedQuotaId =
      typeof window !== "undefined"
        ? localStorage.getItem(LOCAL_QUOTA_ID_KEY)
        : null;

    // Fast path : cache local encore frais
    const cached = quotaCache.get(fingerprint);
    if (cached?.data && Date.now() - cached.timestamp < CACHE_TTL) {
      if (typeof window !== "undefined") {
        localStorage.setItem(LOCAL_QUOTA_ID_KEY, cached.data.id);
      }
      return {
        quota: mapSupabaseRowToGuestQuota(cached.data),
        fingerprint,
      };
    }

    // Chemin unique : RPC SECURITY DEFINER qui encapsule
    // lookup (fingerprint → stable_fingerprint → cached id), création si
    // absente, et refresh metadata. Permet de locker les RLS anon
    // INSERT/UPDATE à service_role.
    try {
      const rpcResult = await Promise.race([
        supabaseRpc<GuestQuotaSupabaseRow | GuestQuotaSupabaseRow[]>(
          "upsert_guest_quota",
          {
            p_fingerprint: fingerprint,
            p_stable_fingerprint: stableFingerprint || null,
            p_user_agent: metadata.userAgent ?? null,
            p_timezone: metadata.timezone ?? null,
            p_language: metadata.language ?? null,
            p_screen_resolution: metadata.screenResolution ?? null,
            p_cached_quota_id: cachedQuotaId,
          },
          { timeout: 2000, requireAuth: false },
        ),
        new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error("Timeout")), 2000);
        }),
      ]);

      const row = Array.isArray(rpcResult) ? rpcResult[0] : rpcResult;
      if (!row) return null;

      quotaCache.set(fingerprint, { data: row, timestamp: Date.now() });
      if (typeof window !== "undefined") {
        localStorage.setItem(LOCAL_QUOTA_ID_KEY, row.id);
      }

      return {
        quota: mapSupabaseRowToGuestQuota(row),
        fingerprint,
      };
    } catch (error: unknown) {
      if (error instanceof Error && error.message.includes("Timeout")) {
        return null;
      }
      logger.error("Failed to upsert guest quota via RPC", "quota", error);
      return null;
    }
  } catch (error) {
    logger.error("Failed to synchronize guest quota", error);
    return null;
  }
}

// ============================================================================
// CORE FUNCTIONS
// ============================================================================

/**
 * Récupère ou crée le quota guest dans Supabase
 */
export async function getOrCreateGuestQuota(): Promise<GuestQuotaData | null> {
  const result = await ensureGuestQuota();
  return result?.quota ?? null;
}

/**
 * Vérifie si l'utilisateur guest peut consommer des crédits
 */
export async function canConsumeCredits(
  action: CreditActionType,
  credits: number,
  metadata?: Record<string, unknown>,
): Promise<{
  allowed: boolean;
  reason?: string;
  currentQuota?: GuestQuotaData;
}> {
  if (shouldBypassGuestQuota()) {
    return { allowed: true };
  }

  try {
    const result = await ensureGuestQuota();
    const quota = result?.quota;

    if (!quota) {
      return { allowed: false, reason: "Failed to fetch quota" };
    }

    const evaluation = evaluateQuotaLimits(quota, action, credits, metadata);
    if (!evaluation.allowed) {
      return { ...evaluation, currentQuota: quota };
    }

    return { allowed: true, currentQuota: quota };
  } catch (error) {
    logger.error("Failed to check credit consumption", error);
    return { allowed: false, reason: "Internal error" };
  }
}

/**
 * Consomme des crédits et met à jour Supabase
 */
export async function consumeGuestCredits(
  action: CreditActionType,
  credits: number,
  metadata?: Record<string, unknown>,
): Promise<{ success: boolean; quota?: GuestQuotaData; error?: string }> {
  if (shouldBypassGuestQuota()) {
    logger.debug("E2E environment: simulating credit consumption", "quota", {
      action,
      credits,
    });
    return { success: true };
  }

  try {
    const syncResult = await ensureGuestQuota();
    const quota = syncResult?.quota;

    if (!quota) {
      return { success: false, error: "Failed to fetch quota" };
    }

    const evaluation = evaluateQuotaLimits(quota, action, credits, metadata);
    if (!evaluation.allowed) {
      logger.warn("Credit consumption denied", "quota", {
        action,
        credits,
        reason: evaluation.reason,
      });
      return { success: false, error: evaluation.reason };
    }

    // RPC atomique : lookup (avec fallback stable_fingerprint) + UPDATE + journal
    // dans une transaction unique avec FOR UPDATE. Empêche les races read-modify-write
    // entre onglets concurrents.
    // Voir migration 20260415_consume_guest_credit_atomic.sql.
    try {
      const stableFingerprint = await getCachedStableFingerprint().catch(
        () => "",
      );

      const updatedRow = await Promise.race([
        supabaseRpc<GuestQuotaSupabaseRow | GuestQuotaSupabaseRow[]>(
          "consume_guest_credit_atomic",
          {
            p_fingerprint: quota.fingerprint,
            p_stable_fingerprint: stableFingerprint || null,
            p_action: action,
            p_credits: credits,
            p_metadata: metadata || {},
          },
          { timeout: 2000, requireAuth: false },
        ),
        new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error("Timeout")), 2000);
        }),
      ]);

      // PostgREST renvoie soit la ligne directement, soit un tableau d'une ligne
      // selon Prefer header. On normalise.
      const row = Array.isArray(updatedRow) ? updatedRow[0] : updatedRow;
      if (!row) {
        throw new Error("RPC consume_guest_credit_atomic returned no row");
      }

      // Mettre à jour le cache
      quotaCache.set(row.fingerprint, {
        data: row,
        timestamp: Date.now(),
      });

      logger.info("Credits consumed (atomic RPC)", "quota", {
        action,
        credits,
        totalCredits: row.total_credits_consumed,
      });

      return {
        success: true,
        quota: mapSupabaseRowToGuestQuota(row),
      };
    } catch (error: unknown) {
      if (error instanceof Error && error.message.includes("Timeout")) {
        // En cas de timeout, retourner immédiatement sans logger (trop verbeux)
        return {
          success: false,
          error: "Timeout: Supabase is slow or unavailable",
        };
      }
      // quota_not_found = la RPC n'a trouvé ni fingerprint ni stable match.
      // Peut arriver si la ligne quota a été supprimée entre ensureGuestQuota et la consommation.
      const message = error instanceof Error ? error.message : String(error);
      if (message.includes("quota_not_found")) {
        logger.warn("RPC: quota_not_found", "quota", {
          fingerprint: quota.fingerprint,
        });
        return { success: false, error: "Quota not found" };
      }
      logger.error("Failed to update guest quota via RPC", "quota", error);
      return { success: false, error: "Failed to update quota" };
    }
  } catch (error) {
    logger.error("Failed to consume credits", "quota", error);
    return { success: false, error: "Internal error" };
  }
}

/**
 * Récupère le journal de consommation
 */
export async function getGuestQuotaJournal(
  limit: number = 100,
): Promise<GuestQuotaJournalEntry[]> {
  // Environnement E2E -> bypass
  if (shouldBypassGuestQuota()) {
    return [];
  }

  try {
    const fingerprint = await getCachedFingerprint();

    const entries = await supabaseSelect<GuestQuotaJournalSupabaseRow>(
      GUEST_QUOTA_JOURNAL_TABLE,
      {
        fingerprint: `eq.${fingerprint}`,
        order: "created_at.desc",
        limit: limit.toString(),
        select: "*",
      },
      { timeout: 2000, requireAuth: false },
    );

    return entries.map((entry) => ({
      id: entry.id,
      guestQuotaId: entry.guest_quota_id,
      fingerprint: entry.fingerprint,
      action: entry.action,
      credits: entry.credits,
      metadata: entry.metadata ?? {},
      createdAt: entry.created_at,
    }));
  } catch (error) {
    logger.error("Failed to get journal", error);
    return [];
  }
}

/**
 * Récupère les limites pour affichage UI
 */
export function getGuestLimits() {
  return { ...GUEST_LIMITS };
}
